import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Stale-chunk recovery.
 *
 * Route pages are code-split into content-hashed chunks. A tab that stays open
 * across a deploy still runs the old index bundle, so its first navigation to a
 * not-yet-loaded page asks for a chunk hash the new deploy no longer serves
 * (the SPA rewrite answers with index.html), and the dynamic import rejects
 * with "Failed to fetch dynamically imported module" (HOLLYWOODTRUCKS-API-G).
 *
 * On such a failure we reload the page once so the tab picks up the current
 * build. A sessionStorage timestamp prevents a reload loop when the chunk is
 * genuinely unavailable (offline, broken deploy): within the window we rethrow
 * and let the ErrorBoundary / Sentry handle it.
 */

const RELOAD_KEY = 'chunk-reload-at';
const RELOAD_WINDOW_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome / Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite CSS preload
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function readLastReload(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

let reloading = false;

/**
 * Reload once to pick up the current deploy. Returns true if a reload was
 * triggered (or is already in progress), false if one already happened within
 * the guard window.
 */
export function reloadForStaleChunk(): boolean {
  if (reloading) return true;
  if (Date.now() - readLastReload() < RELOAD_WINDOW_MS) return false;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable: without a guard, don't risk a reload loop.
    return false;
  }
  reloading = true;
  window.location.reload();
  return true;
}

// Never settles: keeps Suspense on its fallback while the page reloads.
function pendingForever<T>(): Promise<T> {
  return new Promise<T>(() => {});
}

function clearReloadGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // ignore
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().then(
      (module) => {
        // If the global vite:preloadError handler already started a reload,
        // Vite resolves the import with undefined; don't render or report that.
        if (reloading) return pendingForever<{ default: T }>();
        clearReloadGuard();
        return module;
      },
      (error: unknown) => {
        if (reloading || (isChunkLoadError(error) && reloadForStaleChunk())) {
          return pendingForever<{ default: T }>();
        }
        throw error;
      },
    ),
  );
}
